import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Hook for subscribing to a bulk generation job's real-time updates
 *
 * @param {string|null} jobId - The job ID to subscribe to
 * @returns {{ job: Object|null, loading: boolean, error: string|null }}
 */
export function useBulkGenerationJob(jobId) {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!jobId) {
            setJob(null);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        // Subscribe to the job document
        const unsubscribe = onSnapshot(
            doc(db, 'bulkGenerationJobs', jobId),
            (doc) => {
                if (doc.exists()) {
                    setJob({ id: doc.id, ...doc.data() });
                } else {
                    setJob(null);
                    setError('Job not found');
                }
                setLoading(false);
            },
            (err) => {
                console.error('Error subscribing to bulk generation job:', err);
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [jobId]);

    return { job, loading, error };
}

/**
 * Service functions for bulk generation jobs
 */

const SERVER_URL = 'http://localhost:3001';

/**
 * Start a background bulk generation job
 * Returns immediately with jobId - generation continues on server
 */
export async function startBackgroundGeneration(briefs, brand, userId, brandId, planId, products = []) {
    const response = await fetch(`${SERVER_URL}/api/bulk-generation/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, brandId, planId, briefs, brand, products })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to start background generation');
    }

    return response.json(); // { jobId, status, message }
}

/**
 * Get job status from server (fallback if Firestore not working)
 */
export async function getJobStatus(jobId) {
    const response = await fetch(`${SERVER_URL}/api/bulk-generation/${jobId}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to get job status');
    }

    return response.json();
}

/**
 * Cancel a running job
 */
export async function cancelJob(jobId) {
    const response = await fetch(`${SERVER_URL}/api/bulk-generation/${jobId}/cancel`, {
        method: 'POST'
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to cancel job');
    }

    return response.json();
}

/**
 * Hook for tracking active bulk generation jobs for a user
 * This can be used to show a global indicator of running jobs
 * Includes recently completed jobs (last 2 minutes) for user feedback
 */
export function useActiveBulkJobs(userId) {
    const [activeJobs, setActiveJobs] = useState([]);
    const [recentJobs, setRecentJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setActiveJobs([]);
            setRecentJobs([]);
            setLoading(false);
            return;
        }

        // Import query functions dynamically to avoid issues
        const setupListeners = async () => {
            const { collection, query, where, orderBy, onSnapshot: firestoreOnSnapshot, Timestamp } = await import('firebase/firestore');

            // Active jobs (pending or processing)
            const activeQuery = query(
                collection(db, 'bulkGenerationJobs'),
                where('userId', '==', userId),
                where('status', 'in', ['pending', 'processing']),
                orderBy('createdAt', 'desc')
            );

            const unsubscribeActive = firestoreOnSnapshot(activeQuery,
                (snapshot) => {
                    const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setActiveJobs(jobs);
                    setLoading(false);
                },
                (error) => {
                    console.error('Error getting active bulk jobs:', error);
                    setLoading(false);
                }
            );

            // Recently completed jobs (last 2 minutes)
            const twoMinutesAgo = Timestamp.fromDate(new Date(Date.now() - 2 * 60 * 1000));
            const recentQuery = query(
                collection(db, 'bulkGenerationJobs'),
                where('userId', '==', userId),
                where('status', 'in', ['completed', 'failed']),
                where('updatedAt', '>=', twoMinutesAgo),
                orderBy('updatedAt', 'desc')
            );

            const unsubscribeRecent = firestoreOnSnapshot(recentQuery,
                (snapshot) => {
                    const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setRecentJobs(jobs);
                },
                (error) => {
                    // This query might fail if index doesn't exist, that's ok
                    console.log('Recent jobs query not available:', error.message);
                }
            );

            return () => {
                unsubscribeActive();
                unsubscribeRecent();
            };
        };

        let cleanup;
        setupListeners().then(cleanupFn => {
            cleanup = cleanupFn;
        });

        return () => {
            if (cleanup) cleanup();
        };
    }, [userId]);

    // Combine active and recent jobs, removing duplicates
    const allJobs = [...activeJobs];
    recentJobs.forEach(recent => {
        if (!allJobs.find(j => j.id === recent.id)) {
            allJobs.push(recent);
        }
    });

    return { activeJobs: allJobs, loading };
}

export default useBulkGenerationJob;
